import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Shield, UserPlus, UserMinus, PencilSimple, EnvelopeSimple, ShieldCheck, ShieldWarning, ArrowsClockwise, Funnel } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_META = {
  'team.create':           { label: 'Team member added',     Icon: UserPlus,    color: 'text-emerald-700', bg: 'bg-emerald-100' },
  'team.update':           { label: 'Team member edited',    Icon: PencilSimple, color: 'text-blue-700',   bg: 'bg-blue-100' },
  'team.role_change':      { label: 'Role changed',          Icon: ShieldWarning, color: 'text-amber-700', bg: 'bg-amber-100' },
  'team.delete':           { label: 'Team member removed',   Icon: UserMinus,    color: 'text-rose-700',  bg: 'bg-rose-100' },
  'profile.update':        { label: 'Profile updated',       Icon: PencilSimple, color: 'text-blue-700',  bg: 'bg-blue-100' },
  'profile.email_change':  { label: 'Email changed',         Icon: EnvelopeSimple, color: 'text-amber-700', bg: 'bg-amber-100' },
  'auth.approve':          { label: 'Signup approved',       Icon: ShieldCheck,   color: 'text-emerald-700', bg: 'bg-emerald-100' },
  'auth.reject':           { label: 'Signup rejected',       Icon: UserMinus,    color: 'text-rose-700',   bg: 'bg-rose-100' },
  'product.update':        { label: 'Product edited',        Icon: PencilSimple, color: 'text-blue-700',  bg: 'bg-blue-100' },
  'product.media_delete':  { label: 'Product media removed', Icon: UserMinus,    color: 'text-rose-700',  bg: 'bg-rose-100' },
};

const ACTION_OPTIONS = [
  { value: '',  label: 'All actions' },
  { value: 'team.create',          label: 'Team member added' },
  { value: 'team.update',          label: 'Team member edited' },
  { value: 'team.role_change',     label: 'Role changed' },
  { value: 'team.delete',          label: 'Team member removed' },
  { value: 'profile.update',       label: 'Profile updated' },
  { value: 'profile.email_change', label: 'Email changed' },
  { value: 'auth.approve',         label: 'Signup approved' },
  { value: 'auth.reject',          label: 'Signup rejected' },
  { value: 'product.update',       label: 'Product edited' },
  { value: 'product.media_delete', label: 'Product media removed' },
];

const formatChange = (changes) => {
  if (!changes || typeof changes !== 'object') return null;
  const keys = Object.keys(changes);
  if (keys.length === 0) return null;
  return (
    <ul className="text-xs text-[#3B5A54] space-y-0.5 mt-1">
      {keys.map((k) => {
        const v = changes[k];
        const before = v && typeof v === 'object' ? String(v.before ?? '—') : String(v);
        const after  = v && typeof v === 'object' ? String(v.after ?? '—') : '';
        return (
          <li key={k} className="font-mono">
            <span className="font-semibold text-[#052A23]">{k}:</span>{' '}
            <span className="line-through text-[#6D8E87]">{before || '∅'}</span>{' '}
            → <span className="text-[#00a69c]">{after || '∅'}</span>
          </li>
        );
      })}
    </ul>
  );
};

const AuditLogs = () => {
  const { user } = useAuthStore();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (action) params.action = action;
      const r = await axios.get(`${API}/audit-logs`, { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error('Failed to load audit logs', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => { load(); }, [load]);

  if (user?.role !== 'President') {
    return (
      <div className="p-6 lg:p-8" data-testid="audit-logs-forbidden">
        <p className="text-rose-700">Audit logs are restricted to the President.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="audit-logs-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#E2F3F0] flex items-center justify-center text-[#00a69c]">
            <Shield size={26} weight="fill" />
          </div>
          <div>
            <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="audit-logs-title">
              Audit Logs
            </h1>
            <p className="text-sm text-[#6D8E87]">
              Sensitive actions on team members, profile email changes, and signup approvals.
              {total > 0 && <span className="ml-2 text-[#3B5A54]">· {total} entries</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#D5E8E4] rounded-lg">
            <Funnel size={16} className="text-[#6D8E87]" />
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="text-sm bg-transparent outline-none text-[#052A23]"
              data-testid="audit-filter-action"
            >
              {ACTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm text-[#3B5A54] hover:bg-[#F4F9F7] transition"
            data-testid="audit-refresh-btn"
          >
            <ArrowsClockwise size={16} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[#00a69c] py-8 text-center">Loading audit log…</div>
      ) : items.length === 0 ? (
        <div className="p-8 bg-white border border-[#D5E8E4] rounded-xl text-center" data-testid="audit-empty">
          <Shield size={36} className="mx-auto text-[#6D8E87] mb-2" />
          <p className="text-sm text-[#6D8E87]">No matching audit entries.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#D5E8E4] rounded-xl overflow-hidden" data-testid="audit-list">
          <ul className="divide-y divide-[#E2F3F0]">
            {items.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, Icon: Shield, color: 'text-[#3B5A54]', bg: 'bg-[#F4F9F7]' };
              const Icon = meta.Icon;
              return (
                <li key={e.id} className="p-4 flex items-start gap-3" data-testid={`audit-entry-${e.id}`}>
                  <div className={`w-10 h-10 rounded-xl shrink-0 flex items-center justify-center ${meta.bg} ${meta.color}`}>
                    <Icon size={20} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-semibold text-[#052A23]">
                        {meta.label}
                        {e.target_name && (
                          <span className="text-[#3B5A54] font-normal">
                            {' '}— {e.target_name}{e.target_email ? ` <${e.target_email}>` : ''}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[#6D8E87] whitespace-nowrap">
                        {new Date(e.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <p className="text-xs text-[#6D8E87] mt-0.5">
                      by{' '}
                      <span className="text-[#052A23] font-medium">{e.actor_name || e.actor_email || 'system'}</span>
                      {e.actor_role && <span className="text-[#6D8E87]"> ({e.actor_role})</span>}
                      {e.ip && <span className="text-[#6D8E87] ml-2 font-mono">· {e.ip}</span>}
                    </p>
                    {formatChange(e.details?.changes) || (
                      e.details && Object.keys(e.details).length > 0 && !e.details.changes && (
                        <ul className="text-xs text-[#3B5A54] space-y-0.5 mt-1">
                          {Object.entries(e.details).map(([k, v]) => (
                            <li key={k} className="font-mono">
                              <span className="font-semibold text-[#052A23]">{k}:</span> {String(v)}
                            </li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
